import React, { useState, useCallback, useEffect, useContext } from 'react'
import { useLocation, useParams, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import _each from 'lodash.foreach'
// import _filter from 'lodash.filter'
import _map from 'lodash.map'

import { axios } from '../../context/auth'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'

import { DraggableItem, DraggableContainer } from '../../styled/draggable'
import { PrimaryButton, SecondaryButton } from '../../styled/button'
import InputGroup from '../../styled/input-group'
import Select from '../../styled/select'
import OptionsContainer from '../../styled/options-container'
import { context as SongListContext } from '../../context/song-list'
import SearchTags from '../tags/search-tags'
import Duration from './duration'

import FormError from '../../styled/form-error'
import NoteModal from './note-modal'
import computeNumbering from '../../utils/compute-numbering'

import { pageView, PAGES } from '../../utils/analytics-tracker'
import timeDifference from '../../utils/time-difference'

const CustomInputGroup = styled(InputGroup)`
  width: 90%;
`

const TopContainer = styled.div`
  width: 100%;
  max-width: 1100px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const TopLeft = styled.div` 
  width: 50%;
`

const TopRight = styled.div` 
  width: 50%;
`

const ComboSelect = styled.div`
  display: flex;
  align-items: flex-start;
  margin: 0px;
  padding: 0px;
`

const SearchGroup = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: flex-end;
`

const ListItemProp = styled.span`
  margin-left: 10px;
`

const CheckLabel = styled.div`
  display: inline-block;
  label {
    display: inline-block;
    padding-right: 15px;
    white-space: nowrap;
  }
  input {
    vertical-align: middle;
    margin-right: 5px;
  }
  label span {
    vertical-align: middle;
  }
`

const InListContainer = styled(DraggableContainer)`
  float: left;
  width: 48%;
  margin-right: 2%;

  > .target {
    min-height: 400px;
    background: ${({ theme }) => theme.secondaryBackground};
    border: 1px dashed ${({ theme }) => theme.borderColor};
    margin-bottom: 1em;
  }
`
const AvailableListContainer = styled(DraggableContainer)`
  float: left;
  width: 50%;
  min-height: 400px;
  background: ${({ theme }) => theme.secondaryBackground};
`

const ListsContainer = styled.div`
  max-width: 1100px;
`

const DraggableWrap = styled.div`

`

const NoteActions = styled.span`
  margin-left: auto;
  display: flex;
  gap: 6px;
  align-items: center;
`

const NoteActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  padding: 2px 6px;
  color: ${({ theme }) => theme.bodyText};
  opacity: 0.7;
  &:hover {
    opacity: 1;
  }
`

const StyledDraggableItem = styled(DraggableItem)`
  @media(min-width: 700px) {
    padding: 1em;
  }

  .grippy {
      content: '....';
      width: 20px;
      height: 22px;
      display: inline-block;
      overflow: hidden;
      line-height: 5px;
      cursor: move;
      vertical-align: middle;
      font-size: 12px;
      font-family: sans-serif;
      letter-spacing: 2px;
      background: ${({ theme }) => theme.secondaryBackground};
      color: ${({ theme }) => theme.bodyText};
      text-shadow: 1px 0 1px black;
      flex-shrink: 0;
      margin-right: 0;
      margin-left: 0.5em;
    }

    .grippy:after {
      content: '.... .... .... ....';
    }

  &:hover {
    background-color: ${({ theme }) => theme.inputBackground};
    border-color: ${({ theme }) => theme.secondaryBackground};
  }
`
const NoteItem = styled(DraggableItem)`
  background: ${({ theme }) => theme.secondaryBackground};
  border-left: 3px solid ${({ theme, isBreak }) => isBreak ? theme.primary : theme.borderColor};
  font-style: ${({ isBreak }) => isBreak ? 'normal' : 'italic'};
  opacity: 0.9;
  display: flex;
  align-items: center;
  @media(min-width: 700px) {
    padding: 0.75em 1em;
  }
`

const ALPHA_OPTS = [
  { value: '1', label: 'A-Z' },
  { value: '-1', label: 'Z-A' }
]

const DATE_OPTS = [
  { value: '-1', label: 'New' },
  { value: '1', label: 'Old' }
]

const options = [
  { label: 'Title', name: 'title', value: true, getFn: (idx, song) => `${song.title}\t` },
  { label: 'Artist', name: 'artist', value: true, getFn: (idx, song) => `${song.artist}\t` },
  { label: 'Key', name: 'key', value: false, getFn: (idx, song) => `${song.key ? song.key : '...'}\t` },
  { label: 'Tempo', name: 'tempo', value: false, getFn: (idx, song) => `${song.tempo ? song.tempo : '...'}\t` },
  { label: 'Duration', name: 'duration', value: false, getFn: (idx, song) => `${song.duration ? song.duration : '...'}\t` },
  { label: 'Last Rehearsed', name: 'last_rehearsed', value: true, getFn: (idx, song) => `${song.lastRehearsed ? timeDifference(song.lastRehearsed) : '...'}\t` }
]

const getInitStateOptions = (optArr) => {
  const columns = {}
  _each(optArr, opt => {
    columns[opt.name] = opt.value
  })
  return columns
}

const IN_LIST_ID = 'InListId'
const OUT_LIST_ID = 'OutListId'
const ENTRIES = 'entries'
const AVAILABLE = 'availableSongs'

/**
 * Generate a unique ID for SetlistItem entries.
 * Uses crypto.randomUUID when available, falls back to a simple generator.
 */
const generateId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

/**
 * Determines if an entry is a SetlistItem (note/break) vs a song.
 */
const isSetlistItem = (entry) => entry && entry.type === 'note'

/**
 * Returns the draggable ID for an entry.
 * Songs use their _id, SetlistItems use their client-generated _id.
 */
const getDraggableId = (entry) => entry._id

/**
 * Builds the save payload from the entries array.
 * Song entries become { song: id }, note entries become { type, text, isBreak }.
 */
const buildSavePayload = (entries) => {
  return entries.map(entry => {
    if (isSetlistItem(entry)) {
      return { type: 'note', text: entry.text, isBreak: entry.isBreak }
    }
    return { song: entry._id }
  })
}

/**
 * Normalizes API response data into an entries array.
 * Handles both new `entries` format and legacy `songs`-only format.
 */
const normalizeEntries = (data) => {
  if (data.entries && data.entries.length > 0) {
    return data.entries.map(e => {
      if (e.song) {
        // Song entry - find the populated song object from the songs array
        const songId = e.song._id || e.song
        const songObj = (data.songs || []).find(s => (s._id || s) === songId)
        return songObj || { _id: songId }
      }
      // Note/break entry - ensure it has a client-side _id for dragging
      return { ...e, _id: e._id || generateId() }
    })
  }
  if (data.songs && data.songs.length > 0) {
    // Legacy format: songs array contains populated song objects
    return data.songs
  }
  return []
}

/**
 * Extracts only song entries from the entries array (for filtering available songs).
 */
const getSongEntries = (entries) => {
  if (!entries) return []
  return entries.filter(e => !isSetlistItem(e))
}

const filterInFromAvailable = (availableSongs, inSongs) => {
  const filtered = []
  _each(availableSongs, available => {
    let found = false
    _each(inSongs, inSong => {
      if (available._id === inSong._id) {
        found = true
      }
    })
    if (!found) {
      filtered.push(available)
    }
  })
  return filtered
}

const EditSongItem = ({ stateColumns, song, songNumber, hideIdx }) => (
  <>
    {!hideIdx && songNumber && <span className='number'>{songNumber}.</span>}
    {_map(options, (column, idx) => {
      if (stateColumns[column.name]) {
        return (<ListItemProp key={`setlist-column-select-${idx}`}>{column.getFn(idx, song)}</ListItemProp>)
      }
    })}
  </>
)

let saveDebounce

const EditSetList = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { setlistid } = useParams()

  const [columns, setColumns] = useState(getInitStateOptions(options))
  const [setlist, setSetlist] = useState({})
  const [errors, setErrors] = useState({})
  const [isSaved, setIsSaved] = useState(true)
  const [availableSongs, setAvailableSongs] = useState([])
  const [entries, setEntries] = useState([])
  const [saveTrigger, setSaveTrigger] = useState(false)
  const [noteModalOpen, setNoteModalOpen] = useState(false)
  const [editingNoteIndex, setEditingNoteIndex] = useState(null)
  const [editingNoteValues, setEditingNoteValues] = useState(null)

  const isEdit = location.pathname.endsWith('edit')
  const { songs, modifyList, tags, sortBy, sortOrder, filter } = useContext(SongListContext)

  useEffect(() => {
    const songEntries = getSongEntries(entries)
    setAvailableSongs(filterInFromAvailable(songs, songEntries))
  }, [songs, entries])

  useEffect(() => {
    if (setlist.songs || setlist.entries) {
      setEntries(normalizeEntries(setlist))
    }
  }, [setlist])

  useEffect(() => {
    pageView(PAGES.SETLIST_EDIT)
    const fetchSetlist = async () => {
      if (setlistid) {
        await axios
          .get(`/api/setlists/${setlistid}`)
          .then(response => {
            if (!response.data.errmsg) {
              setSetlist({
                ...response.data
              })
            }
          })
      }
    }
    fetchSetlist()
  }, [setSetlist])

  const toggle = useCallback((name) => {
    setColumns({
      ...columns,
      [name]: !columns[name]
    })
  }, [columns, setColumns])

  const handleChange = useCallback((event) => {
    setSetlist({
      ...setlist,
      [event.target.name]: event.target.value
    })
    handleSubmit()
  }, [setlist])

  /**
   * Performs the actual save of the setlist
   * resets saved to true when complete
   */
  useEffect(() => {
    if (saveTrigger === false) {
      return
    }
    // TODO - validate!
    const url = isEdit ? `/api/setlists/${setlist._id}` : '/api/setlists'
    const method = isEdit ? 'PUT' : 'POST'
    axios({
      url,
      method,
      data: {
        title: setlist.title,
        entries: buildSavePayload(entries)
      }
    }).then(response => {
      if (!response.data.errmsg) {
        // only redirect if we're creating the set, not updating
        if (!setlist._id) {
          navigate(`/setlists/${response.data._id}`)
        }
        setErrors({})
        setIsSaved(true)
      } else {
        console.log('duplicate')
      }
    }).catch(err => {
      if (err.response.status === 400) {
        // field validations
        setErrors(err.response.data)
      } else {
        // something went wrong, generic error
      }
    })
  }, [saveTrigger])

  const handleSubmit = useCallback((event) => {
    setIsSaved(false)
    if (event) {
      event.preventDefault()
    }
    clearTimeout(saveDebounce)
    saveDebounce = setTimeout(() => {
      setSaveTrigger(Date.now())
    }, 2000)
  }, [])

  const handleAddNote = useCallback(() => {
    setEditingNoteIndex(null)
    setEditingNoteValues(null)
    setNoteModalOpen(true)
  }, [])

  const handleEditNote = useCallback((index) => {
    const entry = entries[index]
    setEditingNoteIndex(index)
    setEditingNoteValues({ text: entry.text, isBreak: entry.isBreak })
    setNoteModalOpen(true)
  }, [entries])

  const handleDeleteNote = useCallback((index) => {
    const updated = [...entries]
    updated.splice(index, 1)
    setEntries(updated)
    handleSubmit()
  }, [entries, handleSubmit])

  const handleNoteSave = useCallback(({ text, isBreak }) => {
    if (editingNoteIndex !== null) {
      // Editing existing note
      const updated = [...entries]
      updated[editingNoteIndex] = { ...updated[editingNoteIndex], text, isBreak }
      setEntries(updated)
    } else {
      // Adding new note
      const newItem = { _id: generateId(), type: 'note', text, isBreak }
      setEntries([...entries, newItem])
    }
    setNoteModalOpen(false)
    setEditingNoteIndex(null)
    setEditingNoteValues(null)
    handleSubmit()
  }, [entries, editingNoteIndex, handleSubmit])

  const handleNoteCancel = useCallback(() => {
    setNoteModalOpen(false)
    setEditingNoteIndex(null)
    setEditingNoteValues(null)
  }, [])

  const moveBetweenLists = useCallback(({ source, dest, drop }) => {
    const fromList = source === AVAILABLE ? availableSongs : entries
    const move = fromList.splice(drop.source.index, 1)
    const toList = dest === AVAILABLE ? availableSongs : entries
    toList.splice(drop.destination.index, 0, move[0])

    if (source === AVAILABLE) {
      setAvailableSongs(fromList)
      setEntries(toList)
    } else {
      setAvailableSongs(toList)
      setEntries(fromList)
    }

    handleSubmit()
  }, [setEntries, setAvailableSongs, entries, availableSongs])

  const onDragEnd = useCallback((drop) => {
    if (drop.reason === 'DROP' && drop.destination) {
      switch (true) {
        // MOVE from OUT to IN
        case drop.source.droppableId === OUT_LIST_ID && drop.destination.droppableId === IN_LIST_ID:
          moveBetweenLists({ source: AVAILABLE, dest: ENTRIES, drop })
          break

        // MOVE from OUT to OUT
        case drop.source.droppableId === OUT_LIST_ID && drop.destination.droppableId === OUT_LIST_ID:
          // do nothing...user can't reorder the available songs
          break

        // MOVE from IN to IN
        case drop.source.droppableId === IN_LIST_ID && drop.destination.droppableId === IN_LIST_ID:
          const current2 = entries // eslint-disable-line
          const move2 = current2.splice(drop.source.index, 1) // eslint-disable-line
          current2.splice(drop.destination.index, 0, move2[0])
          setEntries(current2)
          handleSubmit()
          break

        // MOVE from IN to OUT
        case drop.source.droppableId === IN_LIST_ID && drop.destination.droppableId === OUT_LIST_ID:
          // If it's a SetlistItem (note/break), just remove it — don't add to available
          if (isSetlistItem(entries[drop.source.index])) {
            const updated = [...entries]
            updated.splice(drop.source.index, 1)
            setEntries(updated)
            handleSubmit()
          } else {
            moveBetweenLists({ source: ENTRIES, dest: AVAILABLE, drop })
          }
          break

        default:
      }
    }
    if (drop.reason === 'DROP' && drop.source && !drop.destination) {
      // this is a drag out of the set but NOT to the available list
      const entry = entries[drop.source.index]
      if (isSetlistItem(entry)) {
        // Note/break dragged out — just remove it
        const updated = [...entries]
        updated.splice(drop.source.index, 1)
        setEntries(updated)
      } else {
        // Song dragged out — move to available
        const fromList = entries
        const move = fromList.splice(drop.source.index, 1)
        const toList = availableSongs
        toList.push(move[0])
        setEntries(fromList)
        setAvailableSongs(toList)
      }
      handleSubmit()
    }
  }, [setlist, setSetlist, entries, availableSongs])

  return (
    <div className='EditSetlistForm'>
      <h1>Set List</h1>
      <TopContainer>
        <TopLeft>
          <CustomInputGroup>
            <label htmlFor='title'>Title: </label>
            <input
              type='text'
              name='title'
              value={setlist.title}
              onChange={handleChange}
            />
            {errors.title && <FormError>{errors.title}</FormError>}
          </CustomInputGroup>
          {!isEdit && (
            <PrimaryButton
              onClick={handleSubmit}
            >Save Setlist
            </PrimaryButton>
          )}
        </TopLeft>
        <TopRight>
          <InputGroup>
            <Duration songs={getSongEntries(entries)} />
            <SearchGroup>
              <SearchTags noCreate tags={tags} filter={filter} changeFn={modifyList} />
              <div>
                <label>Sort By</label>
                <ComboSelect>
                  <Select
                    value={{ value: sortBy, label: sortBy === 'sortTitle' ? 'Title' : sortBy === 'sortArtist' ? 'Artist' : sortBy === 'lastRehearsed' ? 'Last Rehearsal' : 'Created At' }}
                    onChange={(option) => modifyList('sortBy', option.value)}
                    options={[
                      { value: 'sortTitle', label: 'Title' },
                      { value: 'sortArtist', label: 'Artist' },
                      { value: 'lastRehearsed', label: 'Last Rehearsal' },
                      { value: 'createdAt', label: 'Created At' }
                    ]}
                  />
                  <Select
                    style={{ width: '45%' }}
                    value={{ value: sortOrder, label: (sortBy === 'sortTitle' || sortBy === 'sortArtist') ? (sortOrder === '1' ? 'A-Z' : 'Z-A') : (sortOrder === '-1' ? 'New' : 'Old') }}
                    onChange={(option) => modifyList('sortOrder', option.value)}
                    options={(sortBy === 'sortTitle' || sortBy === 'sortArtist') ? ALPHA_OPTS : DATE_OPTS}
                  />
                </ComboSelect>
              </div>
            </SearchGroup>
          </InputGroup>
        </TopRight>
      </TopContainer>
      <OptionsContainer>
        {_map(options, (opt, idx) => (
          <CheckLabel key={`check-label-${idx}`}>
            <label htmlFor={opt.name}>
              <input type='checkbox' onChange={() => toggle(opt.name)} name={opt.name} checked={columns[opt.name]} /><span>{opt.label}</span>
            </label>
          </CheckLabel>
        ))}
      </OptionsContainer>
      <ListsContainer>
        <DragDropContext onDragEnd={onDragEnd}>
          <InListContainer>
            <SecondaryButton onClick={handleAddNote} style={{ marginBottom: '10px' }}>
              + Add Note/Break
            </SecondaryButton>
            <Droppable droppableId={IN_LIST_ID}>
              {(provided, snapshot) => (
                <div className='target' ref={provided.innerRef}>
                  {_map(computeNumbering(entries), ({ entry: x, songNumber }, i) => (
                    <Draggable key={getDraggableId(x)} draggableId={getDraggableId(x)} index={i}>
                      {(provided, snapshot) => (
                        <DraggableWrap>
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            {isSetlistItem(x) && (
                              <NoteItem key={`note_${x._id}`} isBreak={x.isBreak}>
                                <span>{x.isBreak ? `— ${x.text || 'Set Break'} —` : `[Note] ${x.text}`}</span>
                                <NoteActions>
                                  <NoteActionButton onClick={() => handleEditNote(i)} title='Edit'>✎</NoteActionButton>
                                  <NoteActionButton onClick={() => handleDeleteNote(i)} title='Delete'>✕</NoteActionButton>
                                </NoteActions>
                              </NoteItem>
                            )}
                            {!isSetlistItem(x) && (
                              <StyledDraggableItem key={`song_${x._id}`}><EditSongItem song={x} stateColumns={columns} songNumber={songNumber} /></StyledDraggableItem>
                            )}
                          </div>
                          {provided.placeholder}
                        </DraggableWrap>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>

            {isEdit && (
              <PrimaryButton
                disabled={isSaved}
                onClick={handleSubmit}
              >{isSaved ? 'All changes saved' : 'Save set list'}
              </PrimaryButton>
            )}
          </InListContainer>
          <AvailableListContainer>
            <Droppable droppableId={OUT_LIST_ID}>
              {(provided, snapshot) => (
                <div ref={provided.innerRef}>
                  {_map(availableSongs, (x, i) => (
                    <Draggable key={x._id} draggableId={x._id} index={i}>
                      {(provided, snapshot) => (
                        <DraggableWrap>
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <StyledDraggableItem key={`song_${x._id}`}><EditSongItem song={x} stateColumns={columns} hideIdx /></StyledDraggableItem>
                          </div>
                          {provided.placeholder}
                        </DraggableWrap>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </AvailableListContainer>
          <div style={{ clear: 'both' }} />
        </DragDropContext>
      </ListsContainer>
      <NoteModal
        isOpen={noteModalOpen}
        onSave={handleNoteSave}
        onCancel={handleNoteCancel}
        initialValues={editingNoteValues}
      />
    </div>
  )
}

export default EditSetList
